import { supabase } from './supabaseClient';
import { AssignmentStatus, formatZeitraum } from './assignmentLogic';

/**
 * FREIGABE-SPERRE FÜR BERICHTE AUS FREELANCER-BEGEHUNGEN
 * (ERP/CRM-Plan Abschnitt 7, Workflow Schritt 4: „interner Koordinator prüft
 *  den Bericht (geprueft/nachbesserung); ERST DANACH Versand an den Kunden
 *  über den bestehenden E-Mail-Fluss.")
 *
 * ─── Warum das eine eigene Prüfung ist ──────────────────────────────────────
 * Die Vier-Augen-Prüfung ist im Backend vollständig durchgesetzt: ein
 * Freelancer kommt nie an `geprueft` (set_assignment_status, Matrix). Nur
 * schützt das die falsche Seite. Der Versand an den Kunden läuft von INNEN,
 * mit Admin-Rechten, über denselben Knopf wie jeder andere Bericht — und wer
 * ihn drückt, sieht dem Bericht nicht an, dass er von einem Freelancer stammt
 * und noch niemand hineingesehen hat. Genau dafür ist diese Sperre da.
 *
 * ─── Woran der Bericht erkannt wird ─────────────────────────────────────────
 * Zwei Wege, in dieser Reihenfolge:
 *   1. `report_id` am Auftrag zeigt auf die Berichtszeile (gesetzt beim
 *      Einreichen, soweit der Bericht da schon existiert, sonst über
 *      link_assignment_report nachgetragen). Das ist der eindeutige Weg.
 *   2. Sonst: PROJEKT + BEGEHUNGSTAG. Fällt der Berichtstag in den Zeitraum
 *      eines Auftrags, der noch nicht geprüft ist, gehört der Bericht mit
 *      grosser Wahrscheinlichkeit dazu. Dieser zweite Weg ist der wichtigere:
 *      Berichte entstehen erst BEIM VERSAND (report_history), zum Zeitpunkt
 *      des Einreichens gibt es also meistens noch keine Zeile, auf die
 *      `report_id` zeigen könnte.
 *
 * ─── Welche Status sperren — und welche bewusst NICHT ───────────────────────
 * Gesperrt wird bei `durchgefuehrt`, `bericht_eingereicht` und
 * `nachbesserung`: in genau diesen drei Zuständen hat der Freelancer die
 * Begehung nachweislich durchgeführt, und der Bericht trägt seine Erfassung.
 *
 * NICHT gesperrt wird bei:
 *   • `angeboten` / `angenommen` — die Begehung hat (laut Status) noch nicht
 *     stattgefunden. Ein Bericht von diesem Tag stammt dann vom Innendienst.
 *     Hier zu sperren hiesse, jedem internen Koordinator den Versand zu
 *     verbieten, sobald für dieselbe Woche irgendjemand beauftragt ist — ein
 *     Fehlalarm mit Betriebsstillstand.
 *   • `abgelehnt` / `storniert` — der Auftrag ist folgenlos beendet.
 *   • `geprueft` / `abgerechnet` — genau der Zustand, den die Sperre verlangt.
 *
 * ─── Fehlerverhalten: NICHT sperren ─────────────────────────────────────────
 * Schlägt die Abfrage fehl (offline, RLS, Tabelle fehlt, weil #21 noch nicht
 * gelaufen ist), wird NICHT gesperrt. Gleiche Regel wie in sendGuard.ts: ein
 * Netz- oder Schema-Fehler darf einen legitimen Versand nicht ausbremsen. Die
 * Sperre ist eine zusätzliche Sicherung, nicht die einzige — die eigentliche
 * Durchsetzung liegt in der Übergangsmatrix.
 *
 * Die Entscheidungslogik ist pur (evaluateReportRelease) und ohne DOM/Supabase
 * testbar; nur fetchReleaseEvidence spricht mit der Datenbank.
 */

/** Eine Auftragszeile, so schmal wie die Entscheidung sie braucht. */
export interface ReleaseAssignmentRow {
    id: string;
    status: AssignmentStatus;
    /** ISO-Datum, Beginn EINGESCHLOSSEN. */
    zeitraumVon: string;
    /** ISO-Datum, Ende EINGESCHLOSSEN. */
    zeitraumBis: string;
    reportId?: string | null;
    /** Name des Freelancers, soweit bekannt — nur für den Hinweistext. */
    freelancerName?: string | null;
}

export interface ReleaseEvidence {
    assignments: ReleaseAssignmentRow[];
    /** Abfrage fehlgeschlagen. Dann wird NICHT gesperrt (s. Kopf). */
    lookupFailed?: boolean;
}

export interface ReleaseQuery {
    /** Begehungstag des Berichts (YYYY-MM-DD oder ISO). */
    reportDate?: string | null;
    /** id der Archivzeile, falls der Bericht schon eine hat. */
    reportId?: string | null;
}

export type ReleaseReason = 'none' | 'report_id' | 'zeitraum';

export interface ReleaseDecision {
    /** true = Versand blockieren. */
    blockiert: boolean;
    reason: ReleaseReason;
    /** Status des sperrenden Auftrags; null, wenn nichts sperrt. */
    status: AssignmentStatus | null;
    assignmentId: string | null;
    /** Fertiger deutscher Hinweistext für den Dialog; null, wenn frei. */
    hinweis: string | null;
}

const FREI: ReleaseDecision = {
    blockiert: false,
    reason: 'none',
    status: null,
    assignmentId: null,
    hinweis: null,
};

/**
 * Status, in denen der Freelancer die Begehung nachweislich durchgeführt hat
 * und der Bericht deshalb erst nach der Prüfung hinaus darf.
 */
export const SPERRENDE_STATUS: AssignmentStatus[] = [
    'durchgefuehrt',
    'bericht_eingereicht',
    'nachbesserung',
];

/** Status, die den Versand ausdrücklich FREIGEBEN. */
export const FREIGEGEBENE_STATUS: AssignmentStatus[] = ['geprueft', 'abgerechnet'];

const toYmd = (value?: string | null): string | null => {
    if (!value) return null;
    const ymd = String(value).slice(0, 10);
    return /^\d{4}-\d{2}-\d{2}$/.test(ymd) ? ymd : null;
};

const STATUS_HINWEIS: Partial<Record<AssignmentStatus, string>> = {
    durchgefuehrt:
        'Die Begehung ist durchgeführt, der Bericht wurde aber noch nicht zur Prüfung eingereicht.',
    bericht_eingereicht:
        'Der Bericht liegt zur Vier-Augen-Prüfung und ist noch nicht freigegeben.',
    nachbesserung:
        'Der Bericht wurde zur Nachbesserung zurückgegeben und ist noch nicht wieder eingereicht.',
};

const beschreibe = (a: ReleaseAssignmentRow): string => {
    const wer = (a.freelancerName || '').trim();
    const zeitraum = formatZeitraum(a.zeitraumVon, a.zeitraumBis);
    const kopf = wer
        ? `Dieser Bericht gehört zu einer Freelancer-Begehung von ${wer}`
        : 'Dieser Bericht gehört zu einer Freelancer-Begehung';
    const wann = zeitraum ? ` (${zeitraum})` : '';
    const grund = STATUS_HINWEIS[a.status]
        || 'Der Auftrag ist noch nicht geprüft.';
    return `${kopf}${wann}. ${grund} Erst nach der Freigabe durch den Innendienst darf er an den Kunden gehen.`;
};

/**
 * Die Regel. Reihenfolge zählt: eine Verknüpfung über `report_id` ist eine
 * Tatsache, der Zeitraum-Treffer nur ein sehr guter Hinweis.
 */
export const evaluateReportRelease = (
    query: ReleaseQuery,
    evidence: ReleaseEvidence,
): ReleaseDecision => {
    if (evidence.lookupFailed) return FREI;
    const rows = (evidence.assignments || []).filter(Boolean);
    if (rows.length === 0) return FREI;

    // 1. Eindeutige Verknüpfung.
    const reportId = (query.reportId || '').trim();
    if (reportId) {
        const verknuepft = rows.find((a) => (a.reportId || '') === reportId);
        if (verknuepft) {
            if (FREIGEGEBENE_STATUS.includes(verknuepft.status)) return FREI;
            if (SPERRENDE_STATUS.includes(verknuepft.status)) {
                return {
                    blockiert: true,
                    reason: 'report_id',
                    status: verknuepft.status,
                    assignmentId: verknuepft.id,
                    hinweis: beschreibe(verknuepft),
                };
            }
            // angeboten/angenommen/abgelehnt/storniert: keine Sperre, aber auch
            // kein Weitersuchen — die Verknüpfung ist die stärkere Aussage.
            return FREI;
        }
    }

    // 2. Projekt + Begehungstag.
    const tag = toYmd(query.reportDate);
    if (!tag) return FREI;

    const treffer = rows
        .filter(
            (a) =>
                SPERRENDE_STATUS.includes(a.status) &&
                !!toYmd(a.zeitraumVon) &&
                !!toYmd(a.zeitraumBis) &&
                toYmd(a.zeitraumVon)! <= tag &&
                tag <= toYmd(a.zeitraumBis)!,
        )
        .sort((a, b) => (a.zeitraumVon || '').localeCompare(b.zeitraumVon || ''));

    const hit = treffer[0];
    if (!hit) return FREI;

    return {
        blockiert: true,
        reason: 'zeitraum',
        status: hit.status,
        assignmentId: hit.id,
        hinweis: beschreibe(hit),
    };
};

/**
 * Liest die Begehungsaufträge des Projekts (schmale Spaltenauswahl) samt
 * Freelancer-Namen für den Hinweistext.
 *
 * Der eingebettete Join `freelancers(name)` ist der PostgREST-Weg über den
 * Fremdschlüssel; scheitert er (Beziehung nicht im Schema-Cache), fällt die
 * Abfrage auf die reine Auftragszeile zurück statt den Versand zu sperren.
 */
export const fetchReleaseEvidence = async (projectId: string): Promise<ReleaseEvidence> => {
    const map = (rows: any[]): ReleaseAssignmentRow[] =>
        (rows || []).map((r) => ({
            id: String(r.id),
            status: r.status,
            zeitraumVon: r.zeitraum_von,
            zeitraumBis: r.zeitraum_bis,
            reportId: r.report_id ?? null,
            freelancerName: r.freelancers?.name ?? null,
        }));

    try {
        const mitNamen = await supabase
            .from('inspection_assignments')
            .select('id, status, zeitraum_von, zeitraum_bis, report_id, freelancers(name)')
            .eq('project_id', projectId);
        if (!mitNamen.error) return { assignments: map(mitNamen.data || []) };

        const schlicht = await supabase
            .from('inspection_assignments')
            .select('id, status, zeitraum_von, zeitraum_bis, report_id')
            .eq('project_id', projectId);
        if (schlicht.error) throw schlicht.error;
        return { assignments: map(schlicht.data || []) };
    } catch (e) {
        console.warn(
            'Freigabe-Prüfung des Begehungsberichts fehlgeschlagen — Versand wird NICHT blockiert:',
            e,
        );
        return { assignments: [], lookupFailed: true };
    }
};

/** Komfort-Einstieg für die UI: Abfrage + Bewertung. */
export const checkReportRelease = async (
    projectId: string | null | undefined,
    query: ReleaseQuery,
): Promise<ReleaseDecision> => {
    if (!projectId || projectId === 'temp-id') return FREI;
    return evaluateReportRelease(query, await fetchReleaseEvidence(projectId));
};
